import React, { createContext, useContext, useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  ArrowLeftIcon,
  Bars3Icon,
  Cog6ToothIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import WorkflowNotificationBell from '../workflows/WorkflowNotificationBell';
import EmailVerificationBanner from '../EmailVerificationBanner';
import { billing } from '../../services/api';
import AccountMenu from './AccountMenu';
import { buildSettingsSections, firstAdminHref, isNavItemActive, quickLinks } from './platformNav';

/*
 * The ChatGPT-style navigation (Rudy 2026-11-03, Phil's ask; design "Option A"; the top bar stays and each person
 * switches in the account menu). The chat is the app: its own list column carries the brand and the bell on top and
 * the account, the admin shield and Settings at its foot (ChatNavContext, read by the chat's LeftSidebar). Every other
 * page opens in Settings: its sections listed on the left (searchable), the page on the right, Back to chat on top.
 */

export const ChatNavContext = createContext(null);
export const useChatNav = () => useContext(ChatNavContext);

const SETTINGS_HOME = '/account';

function useMoney(user) {
  const { data } = useQuery(['top-bar-balance', user?.id], () => billing.getBalance().then((r) => r.data), {
    enabled: Boolean(user?.id),
    staleTime: 60_000,
    retry: false,
  });
  const balance = data?.balance;
  return {
    label: 'Credits',
    href: '/credits',
    value: balance == null ? '…' : Number(balance).toLocaleString(undefined, { maximumFractionDigits: 2 }),
  };
}

function Brand() {
  return (
    <Link to="/ai-chat" aria-label="Bluebear.ai, back to chat" className="truncate rounded-lg px-1.5 py-1 text-base font-bold text-gradient hover:bg-gray-800/60">
      🐻 Bluebear.ai
    </Link>
  );
}

/** What the chat's list column shows around itself in sidebar mode. */
export function useChatSidebarChrome({ nav, user, onLogout }) {
  const money = useMoney(user);
  const adminHref = firstAdminHref(nav);
  return useMemo(() => {
    const iconLink = (to, label, Icon) => (
      <Link to={to} title={label} aria-label={label} className="grid h-8 w-8 flex-none place-items-center rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white">
        <Icon className="h-4 w-4" />
      </Link>
    );
    return {
      header: (
        <div className="flex items-center justify-between gap-2 border-b border-gray-700/60 px-2 py-2" data-testid="chat-nav-header">
          <Brand />
          <WorkflowNotificationBell inline />
        </div>
      ),
      footer: (
        <div className="flex items-center gap-1 border-t border-gray-700/60 p-2" data-testid="chat-nav-footer">
          <AccountMenu user={user} items={nav.account} quick={quickLinks(nav)} money={money} onLogout={onLogout} variant="row" />
          {adminHref ? iconLink(adminHref, 'Admin', ShieldCheckIcon) : null}
          {iconLink(SETTINGS_HOME, 'Settings', Cog6ToothIcon)}
        </div>
      ),
      rail: (
        <div className="mt-auto flex flex-col items-center gap-1 pb-1">
          {adminHref ? iconLink(adminHref, 'Admin', ShieldCheckIcon) : null}
          {iconLink(SETTINGS_HOME, 'Settings', Cog6ToothIcon)}
        </div>
      ),
    };
  }, [nav, user, onLogout, money.value, adminHref]); // eslint-disable-line react-hooks/exhaustive-deps
}

function SectionList({ sections, pathname, query, onPick }) {
  const q = query.trim().toLowerCase();
  const shown = sections
    .map((s) => ({ ...s, items: q ? s.items.filter((i) => `${i.name} ${s.label} ${i.description || ''}`.toLowerCase().includes(q)) : s.items }))
    .filter((s) => s.items.length);
  if (!shown.length) return <p className="px-3 py-6 text-center text-xs text-gray-500">Nothing matches “{query}”.</p>;
  return shown.map((section) => (
    <section key={section.id} className="pt-3">
      <h4 className={`mb-1 flex items-center gap-1.5 px-3 text-[11px] font-medium uppercase tracking-wider ${section.admin ? 'text-primary-400' : 'text-gray-500'}`}>
        {section.admin ? <ShieldCheckIcon className="h-3.5 w-3.5" /> : null}
        {section.label}
      </h4>
      {section.items.map((item) => {
        const Icon = item.icon;
        const current = isNavItemActive(pathname, item.href);
        return (
          <Link
            key={`${section.id}:${item.href}`}
            to={item.href}
            onClick={onPick}
            aria-current={current ? 'page' : undefined}
            className={`mx-1.5 flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm ${current ? 'bg-gray-800 font-medium text-white' : 'text-gray-300 hover:bg-gray-800/70 hover:text-white'}`}
          >
            {Icon ? <Icon className="h-4 w-4 flex-none text-gray-500" /> : null}
            <span className="truncate">{item.name}</span>
          </Link>
        );
      })}
    </section>
  ));
}

/** Every page but the chat, in sidebar mode. */
export function SettingsShell({ nav, user, onLogout, frameClass }) {
  const { pathname } = useLocation();
  const sections = useMemo(() => buildSettingsSections(nav), [nav]);
  const money = useMoney(user);
  const [query, setQuery] = useState('');
  const [drawer, setDrawer] = useState(false);

  const aside = (
    <aside className="flex h-full w-72 flex-none flex-col border-r border-gray-800 bg-gray-900" aria-label="Settings">
      <div className="flex items-center justify-between gap-2 px-2 pb-1 pt-2">
        <Link to="/ai-chat" className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-300 hover:bg-gray-800 hover:text-white">
          <ArrowLeftIcon className="h-4 w-4" />
          Back to chat
        </Link>
        <WorkflowNotificationBell inline />
      </div>
      <div className="relative px-3 pb-1 pt-1">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-500" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search settings"
          aria-label="Search settings"
          autoComplete="off"
          className="h-8 w-full rounded-lg border border-gray-800 bg-gray-950/60 pl-8 pr-2 text-sm text-gray-200 outline-none placeholder:text-gray-600 focus:border-primary-600"
        />
      </div>
      <nav aria-label="Settings sections" className="min-h-0 flex-1 overflow-y-auto pb-3">
        <SectionList sections={sections} pathname={pathname} query={query} onPick={() => setDrawer(false)} />
      </nav>
      <div className="border-t border-gray-800 p-2">
        <AccountMenu user={user} items={nav.account} quick={quickLinks(nav)} money={money} onLogout={onLogout} variant="row" />
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen bg-gray-950">
      <div className="hidden md:flex">{aside}</div>
      {drawer ? (
        <div className="fixed inset-0 z-40 flex md:hidden">
          {aside}
          <button type="button" aria-label="Close menu" className="flex-1 bg-black/50" onClick={() => setDrawer(false)} />
        </div>
      ) : null}
      <main className="min-w-0 flex-1 overflow-y-auto">
        <div className="flex items-center gap-2 border-b border-gray-800 px-3 py-2 md:hidden">
          <button type="button" aria-label="Menu" onClick={() => setDrawer((v) => !v)} className="grid h-8 w-8 place-items-center rounded-lg text-gray-400 hover:bg-gray-800">
            {drawer ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
          </button>
          <Brand />
        </div>
        <EmailVerificationBanner />
        <div className={frameClass}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
